import { reactive, ref, nextTick } from 'vue'
import type { PhoneProperties } from '@/components/App/SyncRegisterForm/types'
import type { LeadCreationRequestedTraits } from '@/utils/segment/types'
import { LISTING_SOURCE, SCREEN_CTA } from '@/utils/segment'
import useLogger from '@/composables/useLogger'
interface RegisterFormProps {
  listingIds?: string[]
  listingId?: string
  screen?: string
  totalSavings?: number
  hubCode?: string
  monthlyPayments?: number
  attentionType?: string
}

export default function useRegisterForm (props: RegisterFormProps) {
  const form = reactive({
    phone_is_valid: false,
    phone_prefix: '',
    phone_number: '',
    phone: '',
    email: '',
    name: ''
  })

  const status = ref('form')
  const externalError = ref(null)
  const legalPoliciesAccepted = ref(false)
  const showWarningLegalPolicy = ref(false)

  const setPhoneProperties = (value: PhoneProperties) => {
    form.phone_number = value.phoneNumber
    form.phone_prefix = value.code
  }

  const handlePhoneInputEvent = (newValue: string) => {
    form.phone = newValue
    externalError.value = null
  }

  const opportunityTypeEventMap: Record<string, string> = {
    living: 'LIVING',
    investing: 'INVESTMENT'
  }

  const getLCRProperties = (locale: string, onboardingBedroomsNumber: any, onboardingOpportunityType: any, t: any) => {
    const data: Record<string, any> = {
      phone: form.phone,
      phone_number: form.phone_number,
      phone_prefix: form.phone_prefix,
      screen: props.screen,
      screen_cta: SCREEN_CTA.INVESTMENT_REGISTRATION_FORM,
      location_of_interest_codes: [locale.toUpperCase()],
      budget_currency: t('currency'),
      type_attention: props.attentionType,
      purchase_purpose: onboardingOpportunityType && opportunityTypeEventMap[onboardingOpportunityType] ? opportunityTypeEventMap[onboardingOpportunityType] : 'INVESTMENT_AND_LIVING'
    }

    if (form.name) {
      data.full_name = form.name

      const nameParts = form.name.split(' ')
      data.first_name = nameParts[0]
      data.last_name = nameParts.slice(1).join(' ')
    }

    if (form.email) {
      data.email = form.email.toLowerCase()
    }

    if (props.listingId) {
      data.listing_id = props.listingId
      data.listing_source = LISTING_SOURCE.PROPERTY_CATALOG
    } else if (props.listingIds?.length) {
      data.listing_id = props.listingIds[0]
      data.listing_source = LISTING_SOURCE.PROPERTY_CATALOG
    }

    if (props.hubCode) {
      data.business_hub_code = props.hubCode
    }

    if (props.totalSavings) {
      data.down_payment_amount = props.totalSavings
    }

    if (props.monthlyPayments !== null && props.monthlyPayments !== undefined) {
      data.monthly_payment_budget_max = props.monthlyPayments
    }

    if (onboardingBedroomsNumber) {
      data.bedrooms = onboardingBedroomsNumber
    }

    return data as LeadCreationRequestedTraits
  }

  const sendSlackAlert = (fetchFn: any, title: string, extraPayload = {}) => {
    fetchFn('/api/buyer-front/send-slack-alert', {
      method: 'POST',
      body: {
        title,
        content: {
          userName: form.name,
          userEmail: form.email,
          userPhone: form.phone,
          url: window.location.href,
          ...extraPayload
        }
      }
    })
  }

  const trackLeadCreationRequest = async (trackLeadFn: any, locale: string, onboardingBedroomsNumber: any, onboardingOpportunityType: any, t: any) => {
    try {
      const leadPayload = getLCRProperties(locale, onboardingBedroomsNumber, onboardingOpportunityType, t)
      await trackLeadFn({
        phone: leadPayload.phone,
        listings: [
          leadPayload.listing_id
        ],
        screen: leadPayload.screen,
        screenCta: leadPayload.screen_cta,
        properties: leadPayload
      })

      // @ts-ignore
      window.dataLayer?.push({
        event: 'user-registration-requested',
        phone: leadPayload.phone,
        screen: leadPayload.screen,
        screen_cta: leadPayload.screen_cta
      })
    } catch (error) {
      useLogger().error({
        message: `Error tracking lead: ${error}`
      })
    }
  }

  const createCustomer = async (cdpDataService: any, fetchFn: any, trackLeadFn: any, t: any, locale: string, onboardingBedroomsNumber?: any, onboardingOpportunityType?: any) => {
    const customer = {
      email: form.email,
      phone: form.phone,
      name: form.name
    }

    let response = null
    try {
      status.value = 'loader'
      response = await cdpDataService.createCustomer(customer)
    } catch (error: any) {
      status.value = 'form'
      const errorData = error?.data
      const errorResponse = error?.response

      if (errorData?.code === 'customer_already_exist') {
        return { success: false, error: 'existing_customer' }
      } else if (errorData?.code === 'error_invalid_phone') {
        externalError.value = 'Revisa el país o el número ingresado'
        return { success: false, error: 'invalid_phone' }
      } else if (errorData?.code === 'error_spam_phone') {
        externalError.value = 'El número ingresado ha sido bloqueado por nuestro equipo. Si consideras que esto es un error, por favor contáctanos a servicioalcliente@lahaus.com para recibir asistencia.'
        sendSlackAlert(fetchFn, 'Registration Form | Lead clasificado como SPAM intentó registrarse', { error })
        return { success: false, error: 'spam_phone' }
      } else {
        status.value = 'technical_error'
        let alertContent = 'Registration Form | No fue posible crear el usuario en CDP'
        alertContent += `\n${JSON.stringify(error)}`
        alertContent += `\n${JSON.stringify(errorData)}`
        alertContent += `\n${JSON.stringify(errorResponse)}`
        sendSlackAlert(fetchFn, alertContent, { error })
        return { success: false, error: 'technical_error' }
      }
    }

    if (response?.id) {
      await trackLeadCreationRequest(trackLeadFn, locale, onboardingBedroomsNumber, onboardingOpportunityType, t)

      status.value = 'form'

      const userData = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        id: response?.id
      }

      return { success: true, userData }
    } else {
      sendSlackAlert(fetchFn, 'Registration Form | Usuario se registró pero no se pudo obtener el user id en el response del createCustomer', { ...response })
      return { success: false, error: 'no_user_id' }
    }
  }

  const sendOtpCode = async (fetchFn: any, contactMedia = 'SMS', otpVerificationRef: any) => {
    const isPhoneValid = !!form.phone_number

    if (!isPhoneValid) {
      status.value = 'form'
      return false
    }

    status.value = 'otp'

    await nextTick()

    if (!otpVerificationRef || !otpVerificationRef.value || typeof otpVerificationRef.value.sendOtpCode !== 'function') {
      status.value = 'technical_error'
      sendSlackAlert(fetchFn, 'Registration Form | OTP component reference not available', {})
      return false
    }

    try {
      await otpVerificationRef.value.sendOtpCode(contactMedia)
      return true
    } catch (error: any) {
      status.value = 'technical_error'
      sendSlackAlert(fetchFn, 'Registration Form | Error enviando el OTP', {
        error,
        message: error?.message
      })
      return false
    }
  }

  return {
    form,
    status,
    externalError,
    legalPoliciesAccepted,
    showWarningLegalPolicy,
    setPhoneProperties,
    handlePhoneInputEvent,
    sendSlackAlert,
    createCustomer,
    sendOtpCode,
    trackLeadCreationRequest,
    getLCRProperties
  }
}
